import React, { useCallback } from "react";
import { connect } from "react-redux";

import cn from "classnames";

import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { updateCurrentConfigMatching } from "@app/scene/settings/component/Imports/store/actions";
import {
  areThereMissedFileColumns,
  getImportConfigs,
  getSelectedConfig,
  isConfigModified,
} from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";

import ConfigCard from "./cards/ConfigCard";
import ConfigModifiedCard from "./cards/ConfigModifiedCard";
import styles from "./ConfigBar.module.scss";
import ConfigListPanel from "./ConfigListPanel";
import CreateConfigModal from "./modals/CreateConfigModal";
import SelectMatchingWayModal from "./SelectMatchingWayModal";

interface Props {
  analyticIssuer?: AnalyticsService;
  areThereMissedFileColumns: boolean;
  className?: string;
  configs: ImportConfig[];
  isConfigModified: boolean;
  saveConfig: typeof updateCurrentConfigMatching.request;
  selectedConfig?: ImportConfig;
}

const ConfigBar: React.FC<Props> = ({
  analyticIssuer,
  areThereMissedFileColumns,
  className,
  configs,
  isConfigModified,
  saveConfig,
  selectedConfig,
}) => {
  const [matchingWayModalVisible, , hideMatchingWayModal] = useBoolean(!!configs.length);
  const [createConfigModalVisible, showCreateConfigModal, hideCreateConfigModal] = useBoolean();
  const [configListVisible, showConfigList, hideConfigList] = useBoolean();
  useModalVisibilityAnalyticEffect("Create New Config", createConfigModalVisible, analyticIssuer);

  const handleUseSavedMatching = useCallback(() => {
    hideMatchingWayModal();
    showConfigList();
    analyticIssuer?.clicked(["Use saved matching"]);
  }, [analyticIssuer, hideMatchingWayModal, showConfigList]);

  const handleSaveConfig = useCallback(() => {
    saveConfig();
    analyticIssuer?.clicked(["Save config"]);
  }, [analyticIssuer, saveConfig]);

  const handleStartFromScratch = useCallback(() => {
    hideMatchingWayModal();
    analyticIssuer?.clicked(["Start From Scratch"]);
  }, [analyticIssuer, hideMatchingWayModal]);

  return (
    <>
      {(isConfigModified || selectedConfig) && (
        <div
          className={cn(styles.container, className, {
            [styles.missedColumns]: areThereMissedFileColumns,
            [styles.modified]: isConfigModified && !areThereMissedFileColumns,
          })}
        >
          {isConfigModified ? (
            <ConfigModifiedCard
              config={selectedConfig}
              onCreateNewConfig={showCreateConfigModal}
              onUpdateConfig={handleSaveConfig}
            />
          ) : (
            <ConfigCard config={selectedConfig} />
          )}
        </div>
      )}
      <SelectMatchingWayModal
        onHide={hideMatchingWayModal}
        onStartFromScratch={handleStartFromScratch}
        onUseSavedMatching={handleUseSavedMatching}
        visible={matchingWayModalVisible}
      />
      <CreateConfigModal onHide={hideCreateConfigModal} visible={createConfigModalVisible} />
      <ConfigListPanel
        analyticIssuer={analyticIssuer}
        onHide={hideConfigList}
        visible={configListVisible}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  areThereMissedFileColumns: areThereMissedFileColumns(state),
  configs: getImportConfigs(state),
  isConfigModified: isConfigModified(state),
  selectedConfig: getSelectedConfig(state),
});

const mapDispatchToProps = {
  saveConfig: updateCurrentConfigMatching.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigBar);
